/* global FormData */

const axios = require('axios')

const DEFAULT_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json'
}

const getStatusProblem = status => {
  if (status >= 200 && status <= 299) return null
  if (status >= 400 && status <= 499) return 'CLIENT_ERROR'
  if (status >= 500 && status <= 599) return 'SERVER_ERROR'
  return 'UNKNOWN_ERROR'
}

const getRequestProblem = error => {
  if (axios.isCancel(error) || error.code === 'ERR_CANCELED') return 'CANCEL_ERROR'
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return 'TIMEOUT_ERROR'
  if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') return 'NETWORK_ERROR'
  if (['ENOTFOUND', 'ECONNREFUSED', 'ECONNRESET'].includes(error.code)) return 'CONNECTION_ERROR'
  if (error.response) return getStatusProblem(error.response.status)
  return 'UNKNOWN_ERROR'
}

const normalizeResponse = (response, startedAt) => ({
  duration: Date.now() - startedAt,
  problem: getStatusProblem(response.status),
  ok: response.status >= 200 && response.status <= 299,
  status: response.status,
  headers: response.headers,
  config: response.config,
  data: response.data
})

const normalizeError = (error, startedAt) => {
  if (error.response) return normalizeResponse(error.response, startedAt)

  return {
    duration: Date.now() - startedAt,
    problem: getRequestProblem(error),
    ok: false,
    status: null,
    headers: null,
    config: error.config || null,
    data: null
  }
}

// Preserve the small Apisauce response contract consumed by the existing sagas.
const createHttpClient = ({baseURL, headers = {}, timeout = 0}) => {
  const defaultHeaders = Object.assign({}, DEFAULT_HEADERS, headers)
  const axiosInstance = axios.create({baseURL, headers: defaultHeaders, timeout})

  const request = config => {
    const startedAt = Date.now()
    const requestConfig = Object.assign({}, config, {validateStatus: () => true})

    return axiosInstance.request(requestConfig)
      .then(response => normalizeResponse(response, startedAt))
      .catch(error => normalizeError(error, startedAt))
  }

  const queryRequest = method => (url, params = {}, config = {}) => request(Object.assign({}, config, {
    method,
    url,
    params
  }))

  const dataRequest = method => (url, data = null, config = {}) => {
    const requestConfig = Object.assign({}, config, {method, url, data})

    // Let Axios add the multipart boundary instead of sending the JSON default.
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      requestConfig.headers = Object.assign({}, config.headers, {'Content-Type': undefined})
    }

    return request(requestConfig)
  }

  const setHeader = (name, value) => {
    defaultHeaders[name] = value
    axiosInstance.defaults.headers.common[name] = value
    return axiosInstance
  }

  const setHeaders = nextHeaders => {
    Object.keys(nextHeaders).forEach(name => setHeader(name, nextHeaders[name]))
    return axiosInstance
  }

  const setBaseURL = nextBaseURL => {
    axiosInstance.defaults.baseURL = nextBaseURL
    return axiosInstance
  }

  return {
    axiosInstance,
    headers: defaultHeaders,
    setHeader,
    setHeaders,
    setBaseURL,
    getBaseURL: () => axiosInstance.defaults.baseURL,
    get: queryRequest('get'),
    delete: queryRequest('delete'),
    head: queryRequest('head'),
    post: dataRequest('post'),
    put: dataRequest('put'),
    patch: dataRequest('patch')
  }
}

module.exports = {
  createHttpClient,
  getRequestProblem,
  getStatusProblem
}
